import { createContext, useContext, useState, useEffect } from 'react'
import { translations } from './translations'
import DomTranslator from './DomTranslator'

const LanguageContext = createContext({ lang: 'en', setLang: () => {} })

const STORAGE_KEY = 'orion-lang'

function initialLang() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === 'en' || saved === 'ja') return saved
  } catch {
    /* storage unavailable */
  }
  return 'en'
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLang)

  // Remember the visitor's choice only when they pick a language (not on every page load,
  // so other open tabs never overwrite it).
  const setLang = (next) => {
    setLangState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* storage unavailable */
    }
  }

  // Apply Japanese font class + lang attribute to <html>
  useEffect(() => {
    document.documentElement.classList.toggle('lang-ja', lang === 'ja')
    document.documentElement.setAttribute('lang', lang === 'ja' ? 'ja' : 'en')
  }, [lang])

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
      <DomTranslator lang={lang} />
    </LanguageContext.Provider>
  )
}

/**
 * useLanguage — access translations and the language toggle from any component.
 *
 * Returns:
 *   lang    — current language code ('en' | 'ja')
 *   setLang — change the language
 *   t(key)  — returns { text, className?, style? } for the given key
 *             Falls back to English if the key is missing in current language.
 *
 * Example:
 *   const { t } = useLanguage()
 *
 *   // Text only:
 *   <p>{t('landing.description').text}</p>
 *
 *   // With per-locale className / style overrides:
 *   const entry = t('landing.title')
 *   <h1 className={`text-4xl font-bold ${entry.className ?? ''}`} style={entry.style}>
 *     {entry.text}
 *   </h1>
 */
export function useLanguage() {
  const { lang, setLang } = useContext(LanguageContext)

  function t(key) {
    const entry =
      translations[lang]?.[key] ??
      translations.en[key] ??
      { text: key }

    return {
      text:      entry.text      ?? key,
      className: entry.className ?? undefined,
      style:     entry.style     ?? undefined,
    }
  }

  return { lang, setLang, t }
}
